/**
 * Helpers puros do "CONTEXTO DO RELATÓRIO" do LP
 * (Cliente → Projeto → Unidade/Área → Equipamento → Inspeção).
 *
 * Separados da página para serem exercitados sem DOM. A filtragem por
 * contexto e as checagens de seleção ausente/obsoleta são as mesmas do ME —
 * reaproveitadas, não duplicadas.
 */

import type { LpReportSeed } from "../../application/services/lp-report-service.js";
import { isEligibleToBeNamedPlh } from "../../domain/identity/plh-eligibility.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";

export {
  filterEquipmentsByContext,
  staleMeContextFields as staleLpContextFields
} from "./me-report-context.js";

/**
 * O que falta para criar o LP. A Inspeção NÃO entra aqui: é opcional.
 * Mensagens nomeiam o campo — nunca um "dados inválidos".
 */
export function missingLpContextFields(selection: LpContextSelection): string[] {
  const missing: string[] = [];
  if (!selection.clientId) missing.push("o Cliente");
  if (!selection.projectId) missing.push("o Projeto");
  if (!selection.unitAreaId) missing.push("a Unidade / Área");
  if (!selection.equipmentId) missing.push("o Equipamento / TAG");
  return missing;
}

/** O botão "Criar Relatório LP" habilita com os quatro obrigatórios — com ou sem Inspeção. */
export function canCreateLpFromContext(selection: LpContextSelection): boolean {
  return missingLpContextFields(selection).length === 0;
}

export interface LpContextSelection {
  clientId: string;
  projectId: string;
  unitAreaId: string;
  equipmentId: string;
  inspectionId: string;
}

/** Valor de medida vindo do cadastro de equipamento — número, texto ou {value, unit}. */
function measurementText(input: unknown): string {
  if (input === null || input === undefined || input === "") return "";
  if (typeof input === "number") return String(input).replace(".", ",");
  if (typeof input === "string") return input.trim();
  if (typeof input === "object") {
    const value = (input as any).value;
    if (value === null || value === undefined || value === "") return "";
    return String(value).replace(".", ",");
  }
  return "";
}

/** "Inspeção Periódica — 15/09/2026": o domínio não numera inspeções, então o documento a identifica por tipo e data. */
export function formatInspectionReference(inspection: any): string {
  if (!inspection) return "";
  const type = inspection.inspectionType || inspection.modality || "Inspeção";
  const date = inspection.startDate ? formatCivilDateBR(String(inspection.startDate).slice(0, 10)) : "";
  return [type, date].filter(Boolean).join(" — ");
}

/**
 * Mapeia os cadastros escolhidos para os campos do relatório LP. Nada é
 * inventado: cada valor vem de um registro selecionado, e o que o cadastro
 * não tem fica vazio para o usuário preencher.
 */
export function buildLpSeedFromContext(input: {
  selection: LpContextSelection;
  client?: any;
  project?: any;
  unitArea?: any;
  equipment?: any;
  inspection?: any;
  inspector?: any;
  equipmentTypeLabels?: Record<string, string>;
}): LpReportSeed {
  const { selection, client, project, unitArea, equipment, inspection, inspector } = input;
  const labels = input.equipmentTypeLabels ?? {};

  const material = equipment
    ? (equipment.material || equipment.materialSpecification || equipment.bodyMaterial || "")
    : "";
  const thickness = equipment
    ? (measurementText(equipment.nominalThickness) || measurementText(equipment.outsideDiameter) || measurementText(equipment.nominalDiameter))
    : "";

  return {
    clientId: selection.clientId,
    clientDisplayName: client ? (client.name || client.legalName || "") : "",
    projectId: selection.projectId || null,
    projectContract: project ? `${project.code} — ${project.name}` : "",
    unitAreaId: selection.unitAreaId || null,
    unitArea: unitArea ? `${unitArea.code} — ${unitArea.name}` : "",
    siteLocation: equipment?.location || unitArea?.name || "",
    equipmentId: selection.equipmentId || null,
    equipmentTag: equipment?.tag || "",
    equipmentTypeLabel: equipment ? (labels[equipment.equipmentType] || equipment.equipmentType || "") : "",
    component: equipment?.name || "",
    baseMaterial: material,
    thicknessOrDiameter: thickness,
    inspectionId: selection.inspectionId || null,
    inspectionNumber: formatInspectionReference(inspection),
    inspectorProfessionalId: inspection?.inspectorId || null,
    inspectorName: inspector?.name || ""
  };
}

/**
 * Candidatos a Engenheiro/PLH do relatório: só Profissionais realmente
 * habilitados pelo gate existente para o Cliente do relatório. ADMIN,
 * DEVELOPER ou um ENGINEER sem plhClientId/assinatura não aparecem.
 */
export function plhCandidatesForClient(professionals: readonly any[], clientId: string): any[] {
  return professionals
    // Seletor: quem PODE ser nomeado PLH. O responsável é do Cliente.
    .filter(p => isEligibleToBeNamedPlh(p).allowed)
    .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR"));
}

/** Quem pode trocar os signatários: a permissão central de Engenharia, e só enquanto o relatório é editável. */
export function canManageLpSignatories(hasPermission: boolean, status: string): boolean {
  return hasPermission && status !== "SIGNED";
}

/** Tipos de Profissional compatíveis com a função de Inspetor. */
const INSPECTOR_TYPES = new Set(["INSPECTOR", "NDT_INSPECTOR"]);

export interface SignatoryCandidateGroups {
  preferred: any[];
  others: any[];
}

/**
 * Candidatos a Inspetor de Equipamentos: os Profissionais ativos cadastrados
 * como Inspetor primeiro; os demais ativos em seguida, separados — sem
 * inventar um filtro que o cadastro não tem. O já selecionado nunca some.
 */
export function inspectorCandidates(professionals: readonly any[], currentId?: string | null): SignatoryCandidateGroups {
  const active = professionals.filter(p => p.status === "ACTIVE" || p.id === currentId);
  const byName = (a: any, b: any) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  return {
    preferred: active.filter(p => INSPECTOR_TYPES.has(String(p.professionalType || ""))).sort(byName),
    others: active.filter(p => !INSPECTOR_TYPES.has(String(p.professionalType || ""))).sort(byName)
  };
}

/**
 * Candidatos a Fiscal do Contrato: os Signatários cadastrados para o Cliente
 * (cadastro oficial Administração → Signatários) primeiro; os demais
 * Profissionais ativos em seguida.
 */
export function fiscalCandidates(
  professionals: readonly any[],
  registeredSignatories: readonly { professionalId: string; status?: string }[],
  currentId?: string | null
): SignatoryCandidateGroups {
  const registered = new Set(registeredSignatories.filter(s => (s.status ?? "ACTIVE") === "ACTIVE").map(s => String(s.professionalId)));
  const active = professionals.filter(p => p.status === "ACTIVE" || p.id === currentId);
  const byName = (a: any, b: any) => String(a.name || "").localeCompare(String(b.name || ""), "pt-BR");
  return {
    preferred: active.filter(p => registered.has(String(p.id))).sort(byName),
    others: active.filter(p => !registered.has(String(p.id))).sort(byName)
  };
}

/** "NOME — FUNÇÃO / QUALIFICAÇÃO" para o select. */
export function formatSignatoryOption(p: any): string {
  const qual = p.functionTitle || p.professionalType || "";
  return qual ? `${p.name} — ${qual}` : String(p.name || "");
}

/** Tamanho de arquivo como a IMAGEM 01 mostra: "245 KB". */
export function formatFileSize(bytes: number | null | undefined): string {
  const n = Number(bytes ?? 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
}
